import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { User, MapPin, Heart, Package, LogOut, Plus, Trash2, Loader2 } from "lucide-react";
import { api, inr } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ProductCard } from "@/components/ProductCard";
import { toast } from "sonner";

const emptyAddr = { name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", is_default: false };

export default function Account() {
  const { user, setUser, logout, setAuthOpen } = useStore();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const tab = params.get("tab") || "profile";
  const [profile, setProfile] = useState({ name: "", email: "", phone: "" });
  const [addresses, setAddresses] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [orders, setOrders] = useState([]);
  const [newAddr, setNewAddr] = useState(emptyAddr);
  const [showAddr, setShowAddr] = useState(false);

  useEffect(() => {
    if (!user) { setAuthOpen(true); return; }
    setProfile({ name: user.name || "", email: user.email || "", phone: user.phone || "" });
    api.get("/account/addresses").then((r) => setAddresses(r.data)).catch(() => {});
    api.get("/account/wishlist").then((r) => setWishlist(r.data)).catch(() => {});
    api.get("/account/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, [user]); // eslint-disable-line

  if (!user) return <div className="py-32 text-center text-[#8C7B6E]" data-testid="account-login-required">Please sign in to view your account.</div>;

  const saveProfile = async () => {
    const { data } = await api.put("/account/profile", profile);
    setUser(data);
    toast.success("Profile updated");
  };

  const addAddress = async () => {
    if (!newAddr.name || !newAddr.line1 || !newAddr.pincode) return toast.error("Fill name, address and pincode");
    const { data } = await api.post("/account/addresses", newAddr);
    setAddresses(data); setNewAddr(emptyAddr); setShowAddr(false);
    toast.success("Address saved");
  };
  const delAddress = async (id) => {
    const { data } = await api.delete(`/account/addresses/${id}`);
    setAddresses(data); toast.success("Address removed");
  };

  const tabs = [
    { id: "profile", label: "Profile", icon: User },
    { id: "orders", label: "Orders", icon: Package },
    { id: "addresses", label: "Addresses", icon: MapPin },
    { id: "wishlist", label: "Wishlist", icon: Heart },
  ];

  return (
    <div data-testid="account-page" className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 py-12">
      <div className="flex items-center justify-between mb-10">
        <div>
          <span className="eyebrow">My Account</span>
          <h1 className="font-display text-4xl mt-2">Hello, {user.name}</h1>
        </div>
        <Button onClick={() => { logout(); nav("/"); }} data-testid="logout-btn" className="btn-outline-gold rounded-full"><LogOut size={16} className="mr-2" /> Sign Out</Button>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-8">
        <aside className="lux-card p-3 h-max">
          {tabs.map((t) => (
            <button key={t.id} data-testid={`account-tab-${t.id}`} onClick={() => setParams({ tab: t.id })}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition ${tab === t.id ? "bg-[#D4AF37] text-[#1A0F0B] font-semibold" : "text-[#C8B8AB] hover:bg-[#1A0F0B]"}`}>
              <t.icon size={17} /> {t.label}
            </button>
          ))}
        </aside>

        <div>
          {tab === "profile" && (
            <div className="lux-card p-6 max-w-lg" data-testid="profile-panel">
              <h3 className="font-display text-xl mb-5">Personal Details</h3>
              {["name", "email", "phone"].map((f) => (
                <div key={f} className="mb-4">
                  <label className="text-xs text-[#8C7B6E] uppercase tracking-wider">{f}</label>
                  <Input data-testid={`profile-${f}`} value={profile[f]} onChange={(e) => setProfile({ ...profile, [f]: e.target.value })} className="mt-1 bg-[#1A0F0B] border-[#3B251D] text-[#FDFBF7]" />
                </div>
              ))}
              <Button data-testid="save-profile-btn" onClick={saveProfile} className="btn-gold rounded-full mt-2">Save Changes</Button>
            </div>
          )}

          {tab === "orders" && (
            <div className="space-y-4" data-testid="orders-panel">
              {orders.length === 0 && <p className="text-[#8C7B6E]">No orders yet. <Link to="/shop" className="text-[#D4AF37]">Start shopping</Link></p>}
              {orders.map((o) => (
                <Link to={`/order/${o.order_id}`} key={o.order_id} className="lux-card p-5 flex items-center justify-between hover-lift" data-testid={`order-row-${o.order_id}`}>
                  <div>
                    <p className="font-mono-lux text-[#D4AF37]">{o.order_id}</p>
                    <p className="text-[#8C7B6E] text-xs mt-1">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} · {o.items.length} item(s)</p>
                  </div>
                  <div className="text-right">
                    <p className="font-display text-lg text-[#FDFBF7]">{inr(o.total)}</p>
                    <span className="text-xs capitalize text-[#E6C594]">{o.status}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {tab === "addresses" && (
            <div data-testid="addresses-panel">
              <div className="grid sm:grid-cols-2 gap-4">
                {addresses.map((a) => (
                  <div key={a.id} className="lux-card p-5" data-testid={`address-${a.id}`}>
                    <div className="flex justify-between">
                      <p className="font-semibold text-[#FDFBF7]">{a.name} {a.is_default && <span className="text-[10px] text-[#D4AF37] ml-1">DEFAULT</span>}</p>
                      <button onClick={() => delAddress(a.id)} className="text-[#8C7B6E] hover:text-[#DC2626]"><Trash2 size={16} /></button>
                    </div>
                    <p className="text-[#C8B8AB] text-sm mt-1">{a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} — {a.pincode}</p>
                    <p className="text-[#8C7B6E] text-sm mt-1">{a.phone}</p>
                  </div>
                ))}
              </div>
              {!showAddr ? (
                <Button onClick={() => setShowAddr(true)} data-testid="add-address-btn" className="btn-outline-gold rounded-full mt-5"><Plus size={16} className="mr-2" /> Add Address</Button>
              ) : (
                <div className="lux-card p-6 mt-5 max-w-xl">
                  <h4 className="font-display text-lg mb-4">New Address</h4>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {["name", "phone", "line1", "line2", "city", "state", "pincode"].map((f) => (
                      <Input key={f} data-testid={`new-addr-${f}`} placeholder={f} value={newAddr[f]} onChange={(e) => setNewAddr({ ...newAddr, [f]: e.target.value })}
                        className={`bg-[#1A0F0B] border-[#3B251D] text-[#FDFBF7] ${["line1", "line2"].includes(f) ? "sm:col-span-2" : ""}`} />
                    ))}
                  </div>
                  <div className="flex gap-3 mt-4">
                    <Button onClick={addAddress} data-testid="save-address-btn" className="btn-gold rounded-full">Save</Button>
                    <Button onClick={() => setShowAddr(false)} className="btn-outline-gold rounded-full">Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "wishlist" && (
            <div data-testid="wishlist-panel">
              {wishlist.length === 0 ? <p className="text-[#8C7B6E]">Your wishlist is empty.</p> : (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
                  {wishlist.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
